/**
 * Client configuration module for API URLs and environment settings.
 * Reads from Vite environment variables with proper fallbacks.
 *
 * Environment variables (set in .env or .env.local):
 * - VITE_APP_API_URL: Base URL for the S3 Explorer API server
 * - VITE_APP_ENVIRONMENT: Current environment (development/production)
 * - VITE_ONE_DRIVE_REDIRECT_URI: OneDrive OAuth redirect URI
 *
 * When VITE_APP_API_URL is not set, a production build falls back to the host currently
 * serving the page (`<origin>/s3-explorer`) so the prebuilt package embedded into a host
 * frontend works without passing any build-time env.
 */

const isProduction = import.meta.env.MODE === 'production';

/**
 * Host frontend proxies this S3 Explorer server under `/s3-explorer/`
 * (`location /s3-explorer/` in the host nginx), so the API lives at
 * `<current-host>/s3-explorer/api/...`.
 */
export const DEFAULT_HOST_API_PATH = '/s3-explorer';

/**
 * Resolve the API base URL with this precedence:
 * 1. `VITE_APP_API_URL` — explicit build-time config (dev server, cross-domain deployments).
 * 2. `origin` — production build without env: use the host currently serving the page, so a
 *    prebuilt package needs no build-time env to call the right server.
 * 3. `http://localhost:3000` — no env and no `window` (SSR/tests).
 */
export function resolveApiBaseUrl(envUrl: string | undefined, origin: string | undefined): string {
  if (envUrl) return envUrl;
  if (origin) return `${origin}${DEFAULT_HOST_API_PATH}`;
  return 'http://localhost:3000';
}

/**
 * Origin of the current page for the runtime default. Only used by production builds: the
 * repo dev server does not proxy `/s3-explorer`, so dev keeps requiring VITE_APP_API_URL
 * (see `getApiBaseUrl`).
 */
function getRuntimeOrigin(): string | undefined {
  // if (!isProduction || typeof window === 'undefined') return undefined;
  if (typeof window === 'undefined') return undefined;
  return window.location.origin;
}

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_APP_API_URL;
  const baseUrl = resolveApiBaseUrl(envUrl, getRuntimeOrigin());

  // Dev server without env: keep the old localhost fallback + warning.
  if (!envUrl && !isProduction) {
    console.warn(
      `[S3Explorer Config] VITE_APP_API_URL not set, using fallback: ${baseUrl}`,
      '\nSet VITE_APP_API_URL in .env or .env.local to configure the API URL.',
    );
  }

  return baseUrl;
}

/**
 * API base URL for the S3 Explorer backend.
 * Reads `VITE_APP_API_URL` when set; otherwise, in production builds, defaults to the
 * current host (`<origin>/s3-explorer`) so the embedded package works without env config.
 */
export const API_BASE_URL = getApiBaseUrl();

/**
 * Logs current configuration in non-production environments.
 * Call this during app initialization for debugging.
 */
export function logConfig(): void {
  if (!isProduction) {
    console.debug('[S3Explorer Config]', {
      API_BASE_URL,
      VITE_APP_ENVIRONMENT: import.meta.env.VITE_APP_ENVIRONMENT || 'not set',
      MODE: import.meta.env.MODE,
    });
  }
}

export default {
  API_BASE_URL,
  isProduction,
  logConfig,
};
